<script>
import { mapState, mapActions } from 'vuex';
/* components */
import Dropdown from '@/components/Dropdown';
import Button from '@/components/Button';
import RewardsTable from '@/components/RewardsTable';
import Pagination from '@/components/Pagination';
/* tools */
import { copyToClipboard } from '@/scripts/utils';
import { applyLanguage } from '@/scripts/language';
import { dropdownTimeItems } from '@/scripts/constants/map';
import { getAsset } from '@/scripts/utils';
import {
  createReferral,
  getReferralStats,
  getReferralsList,
} from '@/scripts/api/referrals';
import { sharingProviders } from '@/scripts/constants/map';
import { getShareLink } from '@/scripts/utils/link';
import { setPagesCount } from '@/scripts/methods';

export default {
  name: 'Referrals',
  components: {
    Button,
    RewardsTable,
    Pagination,
    Dropdown,
  },
  data() {
    return {
      referralId: null,
      stats: null,
      items: null,
      period: null,
      /*  */
      page: 1,
      pages: 0,
    };
  },
  watch: {
    page(page) {
      this.updateTable({ page });
    },
  },
  computed: {
    ...mapState([]),
    referralLink() {
      const { VUE_APP_REFERRAL_LINK_BASE: REFERRAL_LINK_BASE } = process.env;
      const { referralId } = this;
      const result = `${REFERRAL_LINK_BASE}/?referrer=${referralId}`;

      return result;
    },
  },
  created() {
    this.dropdownTimeItems = dropdownTimeItems;
    this.sharingProviders = sharingProviders;

    const requests = [createReferral(), this.updateStats(), this.updateTable()];

    Promise.all(requests).then(([id]) => {
      this.referralId = id;
    });
  },
  mounted() {},
  methods: {
    ...mapActions({}),
    changeDateFilter(val) {
      const { value: period } = val;

      this.updateStats({ period });
    },
    updateStats(params) {
      return getReferralStats(params).then(stats => {
        this.stats = stats;
      });
    },
    getSharingHref({ id }) {
      const { referralLink } = this;
      const result = getShareLink(id, referralLink);

      return result;
    },
    updateTable(params = {}) {
      const { page = this.page, period = this.period } = params;
      const props = { page, period };

      return getReferralsList(props).then(data => {
        const { items } = data;
        this.items = items;
        this.setPagesCount(data);
      });
    },
    copyToClipboard,
    applyLanguage,
    getAsset,
    setPagesCount,
  },
};
</script>

<template>
  <div class="referrals">
    <div class="referrals__content">
      <h1 class="referrals__title">{{ applyLanguage('referrals', 'Menu') }}</h1>
      <div class="referrals__text">
        Refer your friends and gain RUSH every time they make a kill! Simply
        share your link and make 10% on all RUSH credited to their account.
      </div>
      <!-- info block -->
      <div class="referrals__row referrals__row--info">
        <div class="info">
          <div class="info__item info__item--link">
            <div class="info__label">My Referral Link</div>
            <div class="info__input">
              <input type="text" class="input-link" :value="referralLink" />
              <Button class="copy" @click="copyToClipboard(referralLink)">
                Copy
              </Button>
            </div>
          </div>
          <div class="info__item info__item--social">
            <div class="info__label">Share Referral Link</div>
            <div class="info__social">
              <a
                v-for="provider in sharingProviders"
                :class="`info__link info__link--${provider}`"
                :href="getSharingHref(provider)"
                :key="provider.id"
                target="_blank"
              >
                <img :src="require(`@/assets/svg/referrals/${provider.id}.svg`)"
              /></a>
            </div>
          </div>
          <div class="info__item info__item--img">
            <img :src="require(`@/assets/svg/referrals/info-bg.svg`)" />
          </div>
        </div>
      </div>
      <!-- // -->

      <!-- dropdown time -->
      <div class="referrals__row referrals__row--time">
        <Dropdown
          :items="dropdownTimeItems"
          :setFirst="false"
          :calendarIcon="true"
          @change="changeDateFilter"
        />
      </div>
      <!-- // -->

      <!-- referals activity -->
      <div class="referrals__row referrals__row--activity">
        <div class="activity">
          <div
            class="activity__item"
            v-for="(value, field) in stats"
            :key="field"
          >
            <div class="activity__img">
              <img :src="getAsset(field, { path: 'referrals' })" />
            </div>
            <div class="activity__content">
              <div class="activity__label">{{ field }}</div>
              <div class="activity__value">
                {{ value }}
                <!-- <span v-show="field === 'earnings'">RUSH</span> -->
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- // -->

      <!-- table -->

      <!-- transactions table -->
      <div class="referrals__row referrals__row--table">
        <RewardsTable :tableBody="items" />
      </div>

      <!-- Pagination -->
      <div class="referrals__row referrals__row--pagination">
        <Pagination :page="page" :pages="pages" @set-page="n => (page = n)" />
      </div>
      <!-- // -->
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '@/styles/variables.scss';
.referrals {
  &__content {
    width: 100%;
    padding: 40px 30px 50px 0;
    flex-grow: 1;
  }

  &__title {
    font-family: Poppins-Light;
    font-size: 36px;
    line-height: 51px;
    color: #ffffff;
    margin: 0 0 6px;
    display: flex;
    align-items: center;
  }

  &__text {
    font-family: Poppins-Regular;
    font-size: 14px;
    line-height: 1.86;
    color: rgba(255, 255, 255, 0.6);
    max-width: 832px;
  }

  &__row {
    &--info {
      margin-top: 40px;
    }

    &--time {
      margin-top: 52px;
      max-width: 214px;
    }

    &--activity {
      margin-top: 20px;
    }

    &--table {
      margin-top: 40px;
    }

    &--pagination {
      width: 100%;
      display: flex;
      justify-content: center;
      margin-top: 50px;
    }

    &--info + &--activity {
      margin-top: 52px;
    }
  }
}

.info {
  display: flex;
  width: 100%;
  border-radius: 12px;
  box-shadow: 0 20px 40px 0 rgba(8, 7, 16, 0.5);
  background-color: #232e33;
  min-height: 133px;
  padding: 25px 20px;
  position: relative;

  &__label {
    font-family: Poppins-Regular;
    font-size: 12px;
    line-height: 17px;
    color: rgba(255, 255, 255, 0.5);
    margin-bottom: 20px;
  }

  &__item {
    & + &--social {
      margin-left: 70px;
    }

    &--social {
      position: relative;
      z-index: 1;
    }

    &--link {
      max-width: 601px;
      width: 100%;
      position: relative;
      z-index: 1;
    }

    &--img {
      position: absolute;
      right: 13px;
      bottom: 0;
      display: flex;

      img {
        display: flex;
        max-width: 100%;
        width: auto;
      }
    }
  }

  &__input {
    display: flex;
    width: 100%;

    .input-link {
      max-width: 478px;
    }

    .copy {
      margin-left: 30px;
    }
  }

  &__social {
    display: flex;
    justify-content: center;
  }

  &__link {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;

    & + & {
      margin-left: 10px;
    }

    img {
      display: flex;
      width: 100%;
      height: 100%;
    }

    &:hover {
      opacity: 0.5;
    }
  }
}

.input-link {
  pointer-events: none;
  user-select: none;
  background: transparent;
  border: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  color: #ffffff;
  padding: 7px 0 10px 0;
  font-family: Poppins-Light;
  font-size: 18px;
  line-height: 25px;
  width: 100%;
}

.activity {
  margin-left: -15px;
  margin-right: -15px;
  display: flex;
  justify-content: space-between;

  &__item {
    flex-grow: 1;
    margin: 0 15px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 24px;
    padding: 17px 20px;
    background-color: #232e33;
    min-height: 110px;
  }

  &__img {
    width: 70px;
    height: 70px;
    border-radius: 12px;
    background-color: rgba(255, 255, 255, 0.09);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    img {
      display: flex;
      max-width: 40px;
      max-height: 40px;
    }
  }

  &__content {
    margin-left: 20px;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding-top: 3px;
  }

  &__label {
    font-family: Poppins-Regular;
    font-size: 12px;
    line-height: 17px;
    color: rgba(255, 255, 255, 0.4);
    margin-bottom: 13px;
    text-transform: capitalize;
  }

  &__value {
    font-family: Poppins-Light;
    font-size: 31px;
    letter-spacing: 1.15px;
    line-height: 44px;
    color: #ffffff;
    display: flex;
    align-items: flex-end;
    flex-wrap: wrap;
    word-break: break-all;

    span {
      font-family: Poppins-Regular;
      font-size: 14px;
      letter-spacing: 0.52px;
      margin-left: 8px;
      line-height: 30px;
    }
  }
}

.table {
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0;

  thead {
    border-bottom: 1px solid rgba(151, 151, 151, 0.3);
  }

  th {
    font-family: Poppins-Regular;
    font-size: 12px;
    line-height: 17px;
    color: rgba(255, 255, 255, 0.4);
    text-align: left;
    padding: 10px 20px;

    &:first-child {
      padding: 10px 0;
    }
  }

  .row {
    td {
      padding: 0;
    }

    td:first-child {
      .data {
        padding-left: 0;
      }
    }
  }
}

.data {
  height: 50px;
  margin-top: 10px;
  display: flex;
  align-items: center;
  font-family: Poppins-Regular;
  font-size: 14px;
  line-height: 20px;
  color: #ffffff;
  padding: 5px 20px;
  display: flex;
  align-items: center;

  &.positive {
    color: #38d5b8;
  }

  &.negative {
    color: #f95862;
  }

  &__value {
    span {
      font-size: 10px;
      color: #bcb5ff;
    }
  }
}

.list {
  width: 100%;
  display: none;
  flex-direction: column;
  align-items: center;

  &__item {
    width: 100%;
    max-width: 340px;

    & + & {
      margin-top: 20px;
    }
  }
}

.card {
  border-radius: 16px;
  border: solid 1px #232e33;
  color: #ffffff;

  &__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    padding: 10px 15px;

    .card__value {
      font-size: 10px;
      line-height: 15px;

      span {
        color: #bcb5ff;
      }
    }
  }

  &__middle {
    padding: 15px 15px 10px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  }

  &__bottom {
    padding: 9px 15px 15px;
  }

  &__value {
    font-family: Poppins-Regular;
    font-size: 14px;
    line-height: 20px;

    &.positive {
      color: #38d5b8;
    }

    &.negative {
      color: #f95862;
    }
  }

  &__label {
    font-family: Poppins-Regular;
    font-size: 12px;
    line-height: 17px;
    color: rgba(255, 255, 255, 0.4);
    margin-bottom: 10px;
  }

  &__item {
    width: 50%;

    & + & {
      text-align: right;
    }
  }
}

@media (max-width: $mobile) {
  .referrals {
    &__content {
      padding: 20px 15px 80px;
    }

    &__title {
      font-size: 21px;
      line-height: 29px;
      margin-bottom: 9px;
    }

    &__text {
      font-size: 12px;
      line-height: 1.67;
    }

    &__row {
      &--info {
        margin-top: 30px;
      }

      &--time {
        margin-top: 40px;
        padding-top: 40px;
        border-top: 1px solid rgba(255, 255, 255, 0.2);
        max-width: none;
        width: 100%;
        display: flex;

        .dropdown {
          min-width: 290px;
        }
      }

      &--activity {
        margin-top: 30px;
      }

      &--table {
        margin-top: 40px;
        padding-top: 40px;
        border-top: 1px solid rgba(255, 255, 255, 0.2);
      }

      &--pagination {
        margin-top: 40px;
      }

      &--info + &--activity {
        margin-top: 30px;
      }
    }
  }

  .info {
    flex-wrap: wrap;
    padding: 20px 15px;

    &__label {
      margin-bottom: 15px;
    }

    &__item {
      width: 100%;

      &--img {
        display: none;
      }

      &--link {
        max-width: 100%;

        .info__label {
          margin-bottom: 0;
        }
      }

      & + &--social {
        margin-left: 0;
        margin-top: 30px;
      }
    }

    &__social {
      justify-content: flex-start;
    }

    &__link {
      width: 44px;
      height: 44px;

      & + & {
        margin-left: 15px;
      }
    }

    &__input {
      align-items: flex-end;
      justify-content: space-between;

      .copy {
        margin-left: 16px;
      }
    }
  }

  .input-link {
    font-size: 14px;
    line-height: 20px;
  }

  .activity {
    flex-wrap: wrap;
    justify-content: center;

    &__item {
      width: 100%;
      min-height: 90px;
      border-radius: 16px;
      padding: 15px;

      & + & {
        margin-top: 15px;
      }
    }

    &__img {
      width: 60px;
      height: 60px;
    }

    &__content {
      margin-left: 15px;
      padding-top: 0;
    }

    &__value {
      font-size: 24px;
      line-height: 33px;
      letter-spacing: 0.89px;

      span {
        font-size: 10px;
        letter-spacing: 0.37px;
        line-height: 22px;
        margin-left: 4px;
        font-family: Poppins-Light;
      }
    }

    &__label {
      margin-bottom: 10px;
    }
  }

  .table {
    display: none;
  }

  .list {
    display: flex;
  }
}
</style>
